import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "./ui/select";
import {
  Upload, FileText, Download, Trash2, CheckCircle,
  AlertTriangle, Shield, Loader2, Lock, Archive, Image, File
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILE_ICONS = {
  pdf: <FileText size={18} className="text-red-400" />,
  jpg: <Image size={18} className="text-blue-400" />,
  jpeg: <Image size={18} className="text-blue-400" />,
  png: <Image size={18} className="text-green-400" />,
};

export default function DocumentManager({ customerId, personType }) {
  const [documents, setDocuments] = useState([]);
  const [compliance, setCompliance] = useState([]);
  const [summary, setSummary] = useState(null);
  const [caseData, setCaseData] = useState(null);
  const [storageConfig, setStorageConfig] = useState(null);
  const [docTypes, setDocTypes] = useState([]);
  const [selectedType, setSelectedType] = useState("");
  const [uploading, setUploading] = useState(false);
  const [sealing, setSealing] = useState(false);
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef(null);

  const fetchDocuments = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/documents/customer/${customerId}`, { withCredentials: true });
      setDocuments(res.data.documents);
      setCompliance(res.data.compliance);
      setSummary(res.data.compliance_summary);
      setCaseData(res.data.case);
      setStorageConfig(res.data.storage_config);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  const fetchDocTypes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/documents/types?person_type=${personType || "fisica"}`, { withCredentials: true });
      setDocTypes(res.data.types);
    } catch {
      // silent
    }
  }, [personType]);

  useEffect(() => {
    fetchDocuments();
    fetchDocTypes();
  }, [fetchDocuments, fetchDocTypes]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = file.name.split(".").pop()?.toLowerCase();
    const accepted = storageConfig?.accepted_types || ["pdf", "jpg", "jpeg", "png"];
    if (!accepted.includes(ext)) {
      toast.error(`Tipo de archivo no permitido. Aceptados: ${accepted.join(", ").toUpperCase()}`);
      fileInputRef.current.value = "";
      return;
    }

    const maxMb = storageConfig?.max_file_size_mb || 25;
    if (file.size > maxMb * 1024 * 1024) {
      toast.error(`El archivo excede el limite de ${maxMb}MB`);
      fileInputRef.current.value = "";
      return;
    }

    if (!selectedType) {
      toast.error("Selecciona un tipo de documento");
      fileInputRef.current.value = "";
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("doc_type", selectedType);
      await axios.post(`${API}/documents/upload/${customerId}`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Documento subido exitosamente");
      setSelectedType("");
      fetchDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir documento");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDownload = async (docId) => {
    try {
      const res = await axios.get(`${API}/documents/${docId}/download`, { withCredentials: true });
      let downloadUrl = res.data.download_url;
      // Handle relative URLs (for local storage)
      if (downloadUrl.startsWith('/api/')) {
        downloadUrl = `${process.env.REACT_APP_BACKEND_URL}${downloadUrl}`;
      }
      window.open(downloadUrl, "_blank");
    } catch {
      toast.error("Error al descargar");
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm("Eliminar este documento? Esta accion no se puede deshacer.")) return;
    try {
      await axios.delete(`${API}/documents/${docId}`, { withCredentials: true });
      toast.success("Documento eliminado");
      fetchDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    }
  };

  const handleSealCase = async () => {
    if (!window.confirm(
      "Sellar expediente?\n\nEsto creara un archivo ZIP con todos los documentos y un manifiesto de integridad (SHA-256). Una vez sellado, no se podran agregar ni eliminar documentos."
    )) return;
    setSealing(true);
    try {
      const res = await axios.post(`${API}/documents/customer/${customerId}/seal-case`, {}, { withCredentials: true });
      toast.success(`Expediente sellado: ${res.data.total_documents} documentos`);
      fetchDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al sellar expediente");
    } finally {
      setSealing(false);
    }
  };

  const handleDownloadCase = async () => {
    if (!caseData?.case_id) return;
    try {
      const res = await axios.get(`${API}/documents/case/${caseData.case_id}/download`, { withCredentials: true });
      window.open(res.data.download_url, "_blank");
    } catch {
      toast.error("Error al descargar expediente");
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-8">
        <div className="flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const isSealed = caseData?.status === "sealed";
  const uploadedTypes = new Set(documents.map((d) => d.doc_type));
  const availableTypes = docTypes.filter((t) => !uploadedTypes.has(t.id));
  const maxMb = storageConfig?.max_file_size_mb || 25;
  const accepted = storageConfig?.accepted_types || ["pdf", "jpg", "jpeg", "png"];
  const totalSize = documents.reduce((s, d) => s + (d.file_size || 0), 0);

  return (
    <div className="space-y-6" data-testid="document-manager">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-white flex items-center gap-2" data-testid="documents-section-title">
            <File size={20} className="text-teal-400" />
            Documentos del Expediente
          </h2>
          <p className="text-sm text-slate-400 mt-0.5">
            {personType === "moral" ? "Persona Moral" : "Persona Fisica"} — {documents.length} documentos ({formatSize(totalSize)})
          </p>
        </div>
        {isSealed && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20" data-testid="sealed-badge">
            <Lock size={14} className="text-emerald-400" />
            <span className="text-sm font-medium text-emerald-400">Expediente Sellado</span>
          </div>
        )}
      </div>

      {/* Compliance Progress */}
      {summary && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5" data-testid="doc-compliance-summary">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-white flex items-center gap-2">
              <Shield size={18} className="text-teal-400" />
              Cumplimiento Documental
            </h3>
            <span className={`text-sm font-medium px-2.5 py-1 rounded-full ${
              summary.is_complete ? "bg-emerald-500/10 text-emerald-400" : "bg-amber-500/10 text-amber-400"
            }`}>
              {summary.percentage}%
            </span>
          </div>
          <div className="w-full h-2.5 bg-slate-800 rounded-full mb-4">
            <div
              className={`h-full rounded-full transition-all duration-500 ${summary.is_complete ? "bg-emerald-500" : "bg-teal-500"}`}
              style={{ width: `${summary.percentage}%` }}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {compliance.map((item) => (
              <div
                key={item.doc_type}
                className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm ${
                  item.uploaded
                    ? "bg-emerald-500/5 text-emerald-400 border border-emerald-500/10"
                    : item.required
                      ? "bg-amber-500/5 text-amber-400 border border-amber-500/10"
                      : "bg-slate-800/50 text-slate-500 border border-slate-700/50"
                }`}
                data-testid={`compliance-${item.doc_type}`}
              >
                {item.uploaded ? (
                  <CheckCircle size={15} className="flex-shrink-0" />
                ) : item.required ? (
                  <AlertTriangle size={15} className="flex-shrink-0" />
                ) : (
                  <div className="w-3.5 h-3.5 rounded-full border border-slate-600 flex-shrink-0" />
                )}
                <span className="truncate">{item.name}</span>
                {!item.required && <span className="text-xs opacity-50 ml-auto">(opcional)</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload Section */}
      {!isSealed && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5" data-testid="doc-upload-section">
          <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
            <Upload size={18} className="text-teal-400" />
            Subir Documento
          </h3>

          {availableTypes.length === 0 && documents.length > 0 ? (
            <div className="text-center py-4">
              <CheckCircle size={24} className="mx-auto mb-2 text-emerald-400" />
              <p className="text-sm text-emerald-400 font-medium">Todos los documentos requeridos han sido subidos</p>
              <p className="text-xs text-slate-500 mt-1">Puedes sellar el expediente</p>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[220px]">
                  <label className="text-sm text-slate-400 mb-1.5 block">Tipo de documento *</label>
                  <Select value={selectedType} onValueChange={setSelectedType}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="doc-type-select">
                      <SelectValue placeholder="Seleccionar tipo..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableTypes.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name} {t.required ? " *" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={accepted.map((t) => `.${t}`).join(",")}
                    onChange={handleUpload}
                    className="hidden"
                    data-testid="doc-file-input"
                  />
                  <Button
                    onClick={() => {
                      if (!selectedType) { toast.error("Selecciona un tipo de documento primero"); return; }
                      fileInputRef.current?.click();
                    }}
                    disabled={uploading || !selectedType}
                    className="bg-teal-600 hover:bg-teal-500 text-white h-10"
                    data-testid="doc-upload-button"
                  >
                    {uploading ? (
                      <><Loader2 size={16} className="mr-2 animate-spin" /> Subiendo...</>
                    ) : (
                      <><Upload size={16} className="mr-2" /> Seleccionar Archivo</>
                    )}
                  </Button>
                </div>
              </div>
              {/* File constraints info */}
              <div className="flex flex-wrap gap-4 mt-3 text-xs text-slate-500">
                <span>Formatos: {accepted.map((t) => t.toUpperCase()).join(", ")}</span>
                <span>Max. por archivo: {maxMb}MB</span>
                <span>Max. expediente: {storageConfig?.max_case_size_mb || 250}MB</span>
              </div>
            </>
          )}
        </div>
      )}

      {/* Uploaded Documents List */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl" data-testid="doc-list">
        <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between">
          <h3 className="text-base font-semibold text-white">Documentos Subidos</h3>
          <span className="text-xs text-slate-500">{documents.length} documentos — {formatSize(totalSize)}</span>
        </div>

        {documents.length === 0 ? (
          <div className="px-5 py-10 text-center" data-testid="no-documents">
            <Upload size={32} className="mx-auto mb-3 text-slate-600" />
            <p className="text-slate-400 text-sm font-medium">No hay documentos subidos</p>
            <p className="text-slate-500 text-xs mt-1">
              Sube los documentos requeridos arriba para completar el expediente
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-800">
            {documents.map((doc) => {
              const ext = doc.file_extension || doc.original_filename?.split(".").pop()?.toLowerCase() || "pdf";
              return (
                <div
                  key={doc.doc_id}
                  className="px-5 py-3.5 flex items-center gap-4 hover:bg-slate-800/30 transition-colors"
                  data-testid={`doc-item-${doc.doc_id}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center flex-shrink-0">
                    {FILE_ICONS[ext] || <FileText size={18} className="text-slate-400" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-200">{doc.doc_type_name}</p>
                    <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                      <span className="text-xs text-slate-500 truncate max-w-[200px]">{doc.original_filename}</span>
                      <span className="text-xs text-slate-600">{formatSize(doc.file_size)}</span>
                      <span className="text-xs text-slate-600 uppercase">{ext}</span>
                      <span className="text-xs text-slate-600">
                        {new Date(doc.created_at).toLocaleDateString("es-MX")}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => handleDownload(doc.doc_id)}
                      className="text-teal-400 hover:text-teal-300 h-8 w-8 p-0"
                      data-testid={`download-doc-${doc.doc_id}`}
                      title="Descargar"
                    >
                      <Download size={15} />
                    </Button>
                    {!isSealed && (
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => handleDelete(doc.doc_id)}
                        className="text-slate-500 hover:text-red-400 h-8 w-8 p-0"
                        data-testid={`delete-doc-${doc.doc_id}`}
                        title="Eliminar"
                      >
                        <Trash2 size={15} />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Seal Case / Case Info */}
      {isSealed ? (
        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-5" data-testid="sealed-case-info">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold text-emerald-400 flex items-center gap-2">
                <Lock size={18} />
                Expediente Sellado
              </h3>
              <div className="mt-2 space-y-1 text-sm text-slate-400">
                <p>ID: <span className="font-mono text-slate-300">{caseData.case_id}</span></p>
                <p>Documentos: <span className="text-slate-300">{caseData.total_documents}</span></p>
                <p>Tamano: <span className="text-slate-300">{formatSize(caseData.zip_size)}</span></p>
                <p>Hash SHA-256: <span className="font-mono text-xs text-slate-500 break-all">{caseData.zip_hash}</span></p>
                <p>Sellado por: <span className="text-slate-300">{caseData.sealed_by_name}</span></p>
                <p>Fecha: <span className="text-slate-300">{new Date(caseData.sealed_at).toLocaleString("es-MX")}</span></p>
              </div>
            </div>
            <Button
              onClick={handleDownloadCase}
              className="bg-emerald-600 hover:bg-emerald-500 text-white"
              data-testid="download-case-button"
            >
              <Archive size={16} className="mr-2" />
              Descargar ZIP
            </Button>
          </div>
        </div>
      ) : summary?.is_complete && documents.length > 0 ? (
        <div className="bg-teal-500/5 border border-teal-500/20 rounded-xl p-5" data-testid="seal-case-section">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold text-teal-400 flex items-center gap-2">
                <Archive size={18} />
                Sellar Expediente
              </h3>
              <p className="text-sm text-slate-400 mt-1">
                Todos los documentos requeridos estan completos. Al sellar se creara un archivo ZIP
                con manifiesto de integridad (SHA-256). Una vez sellado, no se podran modificar documentos.
              </p>
            </div>
            <Button
              onClick={handleSealCase}
              disabled={sealing}
              className="bg-teal-600 hover:bg-teal-500 text-white flex-shrink-0"
              data-testid="seal-case-button"
            >
              {sealing ? (
                <><Loader2 size={16} className="mr-2 animate-spin" /> Sellando...</>
              ) : (
                <><Lock size={16} className="mr-2" /> Sellar Expediente</>
              )}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
